import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useSession } from "../../session/SessionProvider";
import { toast } from "react-toastify";
import { ArrowLeft, Paperclip, Send } from "lucide-react";
import { BASE_URL, CDN_BASE_URL } from "../../config";

// اگر داری، اینجا بزن

export default function OrgTicketDetailPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const token = user?.token;

  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);

  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const fetchTicket = async () => {
    if (!token) return;
    setLoading(true);
    try {
      // ✅ Endpoint: GET /ticket/:id (ادمین هم اجازه دارد)
      const res = await fetch(`${BASE_URL}/ticket/${id}`, {
        headers: {
          "x-access-token": token,
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "FAILED");

      setTicket(json?.data || json);
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const sendMessage = async () => {
    if (!token) return;

    if (!text.trim() && (!files || files.length === 0)) {
      toast.error(
        t("ticket_detail.text_or_file_required", "متن یا فایل لازم است")
      );
      return;
    }

    try {
      const fd = new FormData();
      fd.append("text", text || "");
      files.forEach((f) => fd.append("attachments", f));

      const res = await fetch(`${BASE_URL}/ticket/${id}/message`, {
        method: "POST",
        headers: { "x-access-token": token },
        body: fd,
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "FAILED");

      toast.success(t("ticket_detail.sent", "ارسال شد"));
      setText("");
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";

      setTicket(json?.data || json);
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
    }
  };

  const updateStatus = async (nextStatus) => {
    if (!token) return;
    try {
      // ✅ Endpoint پیشنهادی: PATCH /ticket/:id/status
      const res = await fetch(`${BASE_URL}/ticket/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({ status: nextStatus }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "FAILED");

      toast.success(t("ticket_detail.status_updated", "وضعیت بروزرسانی شد"));
      setTicket(json?.data || json);
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
    }
  };

  console.log(ticket);

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  } space-y-4`;

  const messages = Array.isArray(ticket?.messages) ? ticket.messages : [];

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("ticket_detail.page_title", "جزئیات تیکت")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          {/* Top Bar */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 bg-white border px-3 py-2 rounded-lg hover:bg-gray-50"
            >
              <ArrowLeft size={16} />
              {t("common.back", "بازگشت")}
            </button>

            <div className="flex items-center gap-2">
              <select
                value={ticket?.status || "open"}
                onChange={(e) => updateStatus(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="open">open</option>
                <option value="answered">answered</option>
                <option value="closed">closed</option>
              </select>
            </div>
          </div>

          {/* Ticket Info */}
          <div className="bg-white rounded-xl border p-4">
            {loading ? (
              <div className="text-sm text-gray-500">
                {t("common.loading", "در حال بارگذاری...")}
              </div>
            ) : !ticket ? (
              <div className="text-sm text-gray-500">
                {t("ticket_detail.not_found", "تیکت پیدا نشد")}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <h1 className="text-lg font-bold text-gray-800">
                    {ticket.subject}
                  </h1>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 rounded bg-gray-100">
                      {ticket.category}
                    </span>
                    <span className="px-2 py-1 rounded bg-orange-50 text-orange-700">
                      {ticket.priority}
                    </span>
                    <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">
                      {ticket.status}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-gray-500 mt-2">
                  {ticket.user?.name || ticket.user?.email || ""} •{" "}
                  {ticket.createdAt}
                </div>
              </>
            )}
          </div>

          {/* Messages */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <h2 className="font-semibold text-gray-800">
              {t("ticket_detail.messages", "پیام‌ها")}
            </h2>

            <div className="p-4 space-y-3 max-h-[55vh] overflow-y-auto">
              {messages.map((m, idx) => {
                const mine =
                  String(m?.sender?._id || m?.sender) === String(user?._id);
                return (
                  <div
                    key={idx}
                    className={`flex ${mine ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-xl border p-3 text-sm  ${
                        mine ? "bg-green-100" : "bg-gray-100"
                      } `}
                    >
                      <div className="text-xs text-gray-500 mb-1">
                        {mine ? t("support.you", "شما") : m?.sender?.name}
                      </div>
                      {m.text && <div>{m.text}</div>}
                      {Array.isArray(m.attachments) &&
                        m.attachments.map((a, i) => (
                          <a
                            key={i}
                            href={CDN_BASE_URL + a?.url}
                            target="_blank"
                            rel="noreferrer"
                            className="block text-xs text-blue-600 underline mt-1"
                          >
                            {a?.filename} {t("support.attachment")} #{i + 1}
                          </a>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reply Box */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <h3 className="font-semibold text-gray-800">
              {t("ticket_detail.reply", "ارسال پاسخ")}
            </h3>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder={t(
                "ticket_detail.reply_placeholder",
                "پاسخ خود را بنویسید..."
              )}
            />

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
                >
                  <Paperclip size={16} />
                  {t("ticket_detail.attach", "ضمیمه")}
                </button>

                {files.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {files.length} فایل انتخاب شد
                  </div>
                )}
              </div>

              <button
                onClick={sendMessage}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
              >
                <Send size={16} />
                {t("ticket_detail.send", "ارسال")}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
